<script setup>
import { ref, computed } from 'vue';
import { cn } from '@/lib/utils';
import { Loader2, ArrowUp } from 'lucide-vue-next';

const props = defineProps({
  type: {
    type: String,
    default: 'ref',
  },
  class: String,
  title: String,
  description: String,
  multiple: {
    type: Boolean,
    default: false,
  },
  uploading: {
    type: Boolean,
    default: false,
  },
  progress: {
    type: Number,
    default: 0,
  },
  accept: {
    type: String,
    default: '.png,.jpg,.jpeg,.webp',
  },
});

const emit = defineEmits(['file']);

const isDragOver = ref(false);

function onFile(e) {
  const files = Array.from(e.target.files);
  e.target.value = '';
  emit('file', files);
}

function onDrop(e) {
  e.preventDefault();
  isDragOver.value = false;

  if (props.uploading) return;

  const files = Array.from(e.dataTransfer.files);
  if (files.length > 0) {
    emit('file', files);
  }
}

function onDragOver(e) {
  e.preventDefault();
  if (!props.uploading) {
    isDragOver.value = true;
  }
}

function onDragLeave(e) {
  e.preventDefault();
  // 只有当离开整个容器时才重置状态
  if (!e.currentTarget.contains(e.relatedTarget)) {
    isDragOver.value = false;
  }
}

const percentDisplay = computed(() => {
  // Clamp between 0~100 and round
  return Math.round(Math.max(0, Math.min(100, props.progress ?? 0)));
});
</script>

<template>
  <div :class="cn('w-full flex items-center h-fit shrink-0', props.class)">
    <div
      class="border w-full h-full border-dashed rounded-lg p-4 transition-all duration-200"
      :class="{
        'border-[rgba(0, 0, 0, 0.1)]': isDragOver && !uploading,
        'border-[rgba(0, 0, 0, 0.1)] hover:border-[rgba(0, 0, 0, 0.2)]': !isDragOver && !uploading,
        'border-[rgba(0, 0, 0, 0.1)]': uploading,
      }"
      @drop="onDrop"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @dragenter.prevent
    >
      <label
        class="flex h-full items-center justify-between w-full gap-3 cursor-pointer"
        :class="{ 'pointer-events-none': uploading }"
      >
        <div class="flex flex-col gap-0.5 flex-1">
          <span class="text-sm text-black/80">{{ props.title }}</span>
          <span v-if="uploading" class="text-xs text-black/40">
            {{ $t('attach.uploading') }} {{ percentDisplay }}%
          </span>
          <span v-else-if="isDragOver" class="text-xs text-green-600 font-medium">
            {{ $t('attach.dropFilesHere') || 'Drop files here' }}
          </span>
          <span v-else class="text-xs text-black/40">{{ description }}</span>
        </div>
        <div class="relative w-8 h-8 shrink-0">
          <!-- 圆形进度边框 -->
          <svg v-if="uploading" class="absolute inset-0 w-8 h-8 -rotate-90" viewBox="0 0 32 32">
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="rgba(0, 0, 0, 0.1)"
              stroke-width="2"
            />
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="black"
              stroke-width="2"
              stroke-linecap="round"
              :stroke-dasharray="87.96"
              :stroke-dashoffset="87.96 - (87.96 * percentDisplay) / 100"
              class="transition-all duration-300"
            />
          </svg>
          <!-- 圆形背景和图标 -->
          <div
            class="absolute inset-0 rounded-full bg-black/5 flex items-center justify-center"
            :class="{ 'bg-transparent': uploading }"
          >
            <Loader2 v-if="uploading" class="w-4 h-4 text-black/60 animate-spin" />
            <ArrowUp v-else class="w-4 h-4 text-black/60" />
          </div>
        </div>
        <input
          type="file"
          class="hidden"
          :accept="accept"
          :multiple="multiple"
          @change="onFile"
          :disabled="uploading"
        />
      </label>
    </div>
  </div>
</template>

<style scoped></style>
